import { useState } from "react";
import { motion } from "framer-motion";
import api, { formatErr } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { HardHat, Send, Loader2, X } from "lucide-react";

export default function OnboardingBanner() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [msg, setMsg] = useState("");
  const [sending, setSending] = useState(false);

  // Solo se muestra a gestores/técnicos sin obras asignadas
  if (!user || user.role === "admin") return null;
  if (Array.isArray(user.assigned_obras) && user.assigned_obras.length > 0) return null;
  if (dismissed) return null;

  const request = async () => {
    setSending(true);
    try {
      const r = await api.post("/users/request_access", { mensaje: msg || null });
      toast.success(`Solicitud enviada a ${r.data.notificados} administrador(es).`);
      setOpen(false);
      setMsg("");
      setDismissed(true);
    } catch (e) { toast.error(formatErr(e)); }
    setSending(false);
  };

  return (
    <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
      className="legatum-card p-4 relative overflow-hidden" data-testid="onboarding-banner">
      <div className="absolute top-0 left-0 right-0 h-1" style={{ background: "linear-gradient(90deg, #E08A1F, #B45D0E)" }} />
      <div className="absolute inset-0 pointer-events-none opacity-25"
           style={{ background: "radial-gradient(circle at 100% 0%, #E08A1F33 0%, transparent 60%)" }} />
      <div className="relative flex items-start gap-3 flex-wrap">
        <div className="h-11 w-11 rounded-2xl shrink-0 flex items-center justify-center text-white shadow-md"
             style={{ background: "linear-gradient(135deg, #E08A1F, #B45D0E)" }}>
          <HardHat className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-[240px]">
          <div className="font-extrabold text-base">Aún no tienes obras asignadas</div>
          <p className="text-xs text-[var(--legatum-text-muted)] font-semibold mt-0.5 leading-relaxed">
            Cuando un administrador te asigne obras verás sus datos, alertas y tareas en el dashboard.
            Mientras tanto puedes solicitar acceso y consultar tu calendario y tareas asignadas.
          </p>
          {!open ? (
            <div className="flex flex-wrap gap-2 mt-3">
              <Button onClick={() => setOpen(true)} data-testid="onboarding-open"
                className="font-extrabold text-white border-0 h-8 text-xs"
                style={{ background: "linear-gradient(135deg, #E08A1F, #B45D0E)" }}>
                <Send className="h-3.5 w-3.5 mr-1" /> Solicitar acceso a obras
              </Button>
              <Button variant="ghost" onClick={() => setDismissed(true)} data-testid="onboarding-dismiss"
                className="h-8 text-xs font-bold text-[var(--legatum-text-muted)]">Ocultar</Button>
            </div>
          ) : (
            <div className="mt-3 space-y-2" data-testid="onboarding-form">
              <textarea rows={3} value={msg} onChange={(e) => setMsg(e.target.value)}
                placeholder="Cuéntale al admin qué obras necesitas y para qué (opcional)…"
                data-testid="onboarding-msg"
                className="w-full px-3 py-2 rounded-lg border border-[var(--legatum-border)] bg-[var(--legatum-elevated)] text-sm focus:outline-none focus:ring-2 focus:ring-[#E08A1F]/40" />
              <div className="flex items-center gap-2">
                <Button onClick={request} disabled={sending} data-testid="onboarding-send"
                  className="font-extrabold text-white border-0 h-8 text-xs"
                  style={{ background: "linear-gradient(135deg, #E08A1F, #B45D0E)" }}>
                  {sending ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Send className="h-3.5 w-3.5 mr-1" />}
                  Enviar solicitud
                </Button>
                <Button variant="ghost" onClick={() => { setOpen(false); setMsg(""); }} data-testid="onboarding-cancel"
                  className="h-8 text-xs font-bold text-[var(--legatum-text-muted)]">Cancelar</Button>
              </div>
            </div>
          )}
        </div>
        <button onClick={() => setDismissed(true)} data-testid="onboarding-close"
          className="h-7 w-7 rounded-lg flex items-center justify-center hover:bg-black/[0.04] dark:hover:bg-white/5 text-[var(--legatum-text-muted)] shrink-0">
          <X className="h-4 w-4" />
        </button>
      </div>
    </motion.div>
  );
}
